import React, { useMemo } from 'react';
import { useMapSettings } from 'ff/debrief/hooks';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuItem from '@mui/material/MenuItem';
import {
    AccordionSetting,
    AccordionSettingDetail,
    AccordionSettingSummary,
    AccordionSummaryValue,
    CustomMenuItem,
    SettingCheckbox,
    SettingLabel,
    SettingSubHeader,
} from 'ff/debrief/components/map/settings/shared';
import { AirspaceOptions } from 'ff/debrief/models';

export const AirspaceSettings: React.FC = () => {
    const {
        currentSettings,
        settingsUI,
        toggleAccordion,
        toggleMapSetting,
        toggleAirspaceSetting,
    } = useMapSettings();
    const airspaceSettings = currentSettings?.airspaceFilters as AirspaceOptions;
    const airspaceValue = useMemo(() => {
        const allOff =
            !airspaceSettings?.controlled &&
            !airspaceSettings?.classE &&
            !airspaceSettings?.modeC &&
            !airspaceSettings?.trsa &&
            !airspaceSettings?.pja &&
            !airspaceSettings?.adiz &&
            !airspaceSettings?.suaMoa;
        if (!currentSettings?.airspace || allOff) {
            return 'Off';
        }
        const allOn =
            airspaceSettings?.controlled &&
            airspaceSettings?.classE &&
            airspaceSettings?.modeC &&
            airspaceSettings?.trsa &&
            airspaceSettings?.pja &&
            airspaceSettings?.adiz &&
            airspaceSettings?.suaMoa;

        return allOn ? 'All' : 'Custom';
    }, [currentSettings?.airspace, currentSettings?.airspaceFilters]);
    return (
        <CustomMenuItem>
            <AccordionSetting
                expanded={settingsUI.accordionsExpanded?.airspace}
                onChange={() => toggleAccordion('airspace')}
            >
                <AccordionSettingSummary expandIcon={<ExpandMoreIcon />}>
                    <SettingLabel>Airspace</SettingLabel>
                    <AccordionSummaryValue>
                        {airspaceValue}
                    </AccordionSummaryValue>
                </AccordionSettingSummary>
                <AccordionSettingDetail>
                    <MenuItem>
                        <SettingCheckbox
                            id="showAirspace"
                            displayName="Show Airspace"
                            checked={currentSettings?.airspace}
                            onChange={e =>
                                toggleMapSetting({
                                    settingName: 'airspace',
                                    value: e.target.checked,
                                })
                            }
                        />
                    </MenuItem>
                    <SettingSubHeader>Customize Airspace</SettingSubHeader>
                    <MenuItem>
                        <SettingCheckbox
                            displayName="Controlled"
                            checked={airspaceSettings?.controlled}
                            onChange={() => {
                                toggleAirspaceSetting('controlled');
                            }}
                        />
                    </MenuItem>
                    <MenuItem>
                        <SettingCheckbox
                            id="suaMoa"
                            displayName="SUA/MOA"
                            checked={airspaceSettings?.suaMoa}
                            onChange={() => toggleAirspaceSetting('suaMoa')}
                        />
                    </MenuItem>
                    <MenuItem>
                        <SettingCheckbox
                            displayName="TRSA"
                            checked={airspaceSettings?.trsa}
                            onChange={() => toggleAirspaceSetting('trsa')}
                        />
                    </MenuItem>
                    <MenuItem>
                        <SettingCheckbox
                            id="classE"
                            displayName="Class E"
                            checked={airspaceSettings?.classE}
                            onChange={() => toggleAirspaceSetting('classE')}
                        />
                    </MenuItem>
                    <MenuItem>
                        <SettingCheckbox
                            id="parachuteJumpArea"
                            displayName="Parachute Area"
                            checked={airspaceSettings?.pja}
                            onChange={() => toggleAirspaceSetting('pja')}
                        />
                    </MenuItem>
                    <MenuItem>
                        <SettingCheckbox
                            id="modeC"
                            displayName="Mode C"
                            checked={airspaceSettings?.modeC}
                            onChange={() => toggleAirspaceSetting('modeC')}
                        />
                    </MenuItem>
                    <MenuItem>
                        <SettingCheckbox
                            displayName="ADIZ"
                            checked={airspaceSettings?.adiz}
                            onChange={() => toggleAirspaceSetting('adiz')}
                        />
                    </MenuItem>
                </AccordionSettingDetail>
            </AccordionSetting>
        </CustomMenuItem>
    );
};
